import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { CheckCircle, Circle, AlertCircle, FileText, Settings, MessageSquare, Upload, History, RefreshCw, FileDown, Database } from 'lucide-react';
import type { ConfigItem } from '../types';
import { VendorSpecificForm } from './VendorSpecificForm';
import { AttachmentUpload } from './AttachmentUpload';
import { DataMappingVisualization } from './DataMappingVisualization';
import { CIMessaging } from './CIMessaging';
import { CIAttachmentList } from './CIAttachmentList';
import { QuestionManager } from './QuestionManager';
import { DiscoveryQuestionsPanel } from './DiscoveryQuestionsPanel';
import { AuthorityMappingSummary } from './AuthorityMappingSummary';
import { AuthorityMappingArtifact } from '../types';
import { ScopeDeltaReview } from './ScopeDeltaReview';
import { ChangeOrderBuilder, ChangeOrderDraft } from './ChangeOrderBuilder';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface ConfigItemDetails {
  configItemId: string;
  status: 'not-started' | 'in-progress' | 'ready-for-implementation' | 'in-review' | 'done';
  notes?: string;
  configurationDetails?: string;
  vendor?: 'jira' | 'servicenow' | 'zendesk' | 'ivanti-neurons' | 'bmc-helix' | 'freshservice' | 'custom';
  vendorSpecificData?: Record<string, any>;
  artifacts?: {
    authorityMapping?: AuthorityMappingArtifact;
    [key: string]: any;
  };
  implementedAt?: string;
  implementedBy?: {
    userId: string;
    userEmail: string;
    userName?: string;
  };
}

interface ConfigItemDetailDialogProps {
  item: ConfigItem;
  details: ConfigItemDetails | null;
  isOpen: boolean;
  currentUser: any;
  isConsultant?: boolean;
  accessToken: string;
  companyId: string;
  onClose: () => void;
  onSave: (details: ConfigItemDetails) => Promise<void>;
}

export function ConfigItemDetailDialog({
  item,
  details,
  isOpen,
  currentUser,
  isConsultant = false,
  accessToken,
  companyId,
  onClose,
  onSave
}: ConfigItemDetailDialogProps) {
  const [editedDetails, setEditedDetails] = useState<ConfigItemDetails>(
    details || {
      configItemId: item.id,
      status: 'not-started',
      notes: '',
      configurationDetails: ''
    }
  );
  const [originalDetails, setOriginalDetails] = useState<ConfigItemDetails>(
    details || {
      configItemId: item.id,
      status: 'not-started',
      notes: '',
      configurationDetails: ''
    }
  );
  const [saving, setSaving] = useState(false);

  const [reopeningCO, setReopeningCO] = useState<{id: string, draft: ChangeOrderDraft} | null>(null);

  // Reset to original state when dialog opens or details change
  useEffect(() => {
    if (isOpen) {
      const initialDetails = details || {
        configItemId: item.id,
        status: 'not-started' as const,
        notes: '',
        configurationDetails: ''
      };
      setEditedDetails(initialDetails);
      setOriginalDetails(initialDetails);
    }
  }, [isOpen, details, item.id]);

  // Check if any changes have been made
  const hasChanges = useMemo(() => {
    return (
      editedDetails.status !== originalDetails.status ||
      (editedDetails.notes || '') !== (originalDetails.notes || '') ||
      (editedDetails.configurationDetails || '') !== (originalDetails.configurationDetails || '')
    );
  }, [editedDetails, originalDetails]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedDetails);
      onClose();
    } catch (error) {
      console.error('Error saving config item details:', error);
      alert('Failed to save configuration details');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkImplemented = () => {
    if (!isConsultant) {
      alert('Only consultants can mark items as implemented');
      return;
    }

    if (confirm('Mark this configuration item as done?')) {
      setEditedDetails({
        ...editedDetails,
        status: 'done',
        implementedAt: new Date().toISOString(),
        implementedBy: {
          userId: currentUser.id,
          userEmail: currentUser.email,
          userName: currentUser.name
        }
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in-review':
        return <CheckCircle className="h-5 w-5 text-purple-600" />;
      case 'ready-for-implementation':
        return <CheckCircle className="h-5 w-5 text-blue-600" />;
      case 'in-progress':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'done':
        return <Badge className="bg-green-600">Done</Badge>;
      case 'in-review':
        return <Badge className="bg-purple-600">In Review</Badge>;
      case 'ready-for-implementation':
        return <Badge className="bg-blue-600">Ready for Implementation</Badge>;
      case 'in-progress':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">In Progress</Badge>;
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };

  // Get configuration guidance based on category
  const getConfigurationGuidance = () => {
    switch (item.category) {
      case 'sla':
        return {
          title: 'SLA Configuration Guidance',
          points: [
            'Define target response and resolution times for each priority level',
            'Configure business hours and calendars for SLA calculations',
            'Set up SLA breach notifications and escalations',
            'Document which request types/work types each SLA applies to'
          ],
          placeholderDetails: 'Example:\n- Highest Priority: 30 min response, 4 hour resolution\n- High Priority: 1 hour response, 8 hour resolution\n- Medium Priority: 4 hours response, 24 hour resolution\n- Business Hours: Mon-Fri 9am-5pm EST'
        };
      case 'queue':
        return {
          title: 'Queue Configuration Guidance',
          points: [
            'Create queues for different work organization (Unassigned, My Open, Escalated, etc.)',
            'Define JQL filters for each queue',
            'Specify which agents/teams can access each queue',
            'Document queue naming conventions and purposes'
          ],
          placeholderDetails: 'Example:\n- Unassigned Queue: assignee is EMPTY AND resolution is EMPTY\n- My Open Requests: assignee = currentUser() AND resolution is EMPTY\n- Escalated: priority = Highest AND created < -2h'
        };
      case 'approval':
        return {
          title: 'Approval Workflow Guidance',
          points: [
            'Identify which request types require approval',
            'Define approver roles (manager, specific user, field-based)',
            'Configure approval notification settings',
            'Document approval SLAs and escalation paths'
          ],
          placeholderDetails: 'Example:\n- Hardware requests >$1000: Manager approval required\n- Access requests: Security team approval\n- Change requests: CAB approval for production changes'
        };
      case 'asset':
        return {
          title: 'Asset Management Guidance',
          points: [
            'Define asset object types (Hardware, Software, Licenses)',
            'Configure attributes for each asset type',
            'Set up asset import from existing systems',
            'Link asset fields to request forms'
          ],
          placeholderDetails: 'Example:\n- Hardware: Serial #, Model, Assigned To, Purchase Date\n- Software: License Key, Seat Count, Renewal Date\n- Import from: CSV monthly export from existing CMDB'
        };
      case 'automation':
        return {
          title: 'Automation Rules Guidance',
          points: [
            'Define auto-assignment rules based on request type or content',
            'Configure escalation rules for SLA breaches',
            'Set up automatic status transitions',
            'Document automation triggers and conditions'
          ],
          placeholderDetails: 'Example:\n- Auto-assign password resets to Tier 1 team (round-robin)\n- Auto-escalate to manager if SLA < 10% remaining\n- Auto-transition to Waiting on Customer when comment contains "need more info"'
        };
      case 'notification':
      case 'email':
        return {
          title: 'Notification Configuration Guidance',
          points: [
            'Configure email notification triggers (created, assigned, resolved, etc.)',
            'Set up email templates with appropriate branding',
            'Define email request channels (support@, helpdesk@, etc.)',
            'Configure Slack/Teams integration channels if applicable'
          ],
          placeholderDetails: 'Example:\n- support@company.com → IT Support team\n- hr@company.com → HR team\n- Email templates: Use company branding, professional tone\n- Slack #it-alerts for high priority requests'
        };
      case 'portal':
        return {
          title: 'Portal Customization Guidance',
          points: [
            'Upload company logo and configure brand colors',
            'Organize request types into logical categories',
            'Add help text and instructions on portal pages',
            'Configure announcements for maintenance windows'
          ],
          placeholderDetails: 'Example:\n- Primary Color: #0052CC (company blue)\n- Categories: IT Support, HR, Facilities, Finance\n- Help text on submission page explaining expected response times\n- Announcement banner for planned maintenance'
        };
      case 'knowledge-base':
        return {
          title: 'Knowledge Base Guidance',
          points: [
            'Create FAQs for common requests',
            'Organize articles by category/topic',
            'Configure search to surface relevant articles during request submission',
            'Link articles to related request types'
          ],
          placeholderDetails: 'Example:\n- Articles: Password Reset, VPN Setup, New Hardware Request Process\n- Categories: Getting Started, Troubleshooting, Policies\n- Search boost: Title and tags weighted higher than body content'
        };
      case 'integration':
        return {
          title: 'Integration Configuration Guidance',
          points: [
            'Document integration requirements (Slack workspace, Teams tenant, etc.)',
            'Configure authentication and API keys',
            'Set up field mappings for data sync',
            'Test integrations in non-production environment first'
          ],
          placeholderDetails: 'Example:\n- Slack: Connect to company workspace, post to #it-support channel\n- Confluence: Link KB space KEY-123\n- ServiceNow sync: Map Priority, Status, Assignee fields'
        };
      case 'csat':
        return {
          title: 'CSAT Survey Guidance',
          points: [
            'Define survey questions (rating scale, open feedback)',
            'Configure when surveys are sent (immediately on resolve, 1 day later, etc.)',
            'Set up CSAT reporting dashboards',
            'Document how feedback is reviewed and acted upon'
          ],
          placeholderDetails: 'Example:\n- 5-star rating: How satisfied were you with the resolution?\n- Open text: What could we improve?\n- Send 24 hours after resolution\n- Weekly report to management showing trends'
        };
      case 'dashboard':
        return {
          title: 'Dashboard & Reporting Guidance',
          points: [
            'Define key metrics to track (SLA compliance, resolution time, volume)',
            'Configure dashboard widgets for different audiences',
            'Set up scheduled reports for stakeholders',
            'Document who has access to which dashboards'
          ],
          placeholderDetails: 'Example:\n- Agent Dashboard: My Open Requests, SLA Status, Team Performance\n- Manager Dashboard: Team Metrics, Trending Issues, CSAT Scores\n- Weekly executive report: Volume, SLA %, Top Issues'
        };
      case 'service-catalog':
        return {
          title: 'Service Catalog Guidance',
          points: [
            'Document all services offered by the service desk',
            'Define service owners and SLAs for each service',
            'Link services to related request types',
            'Document availability and support hours'
          ],
          placeholderDetails: 'Example:\n- Email Support: 24/7, Tier 1 Team, 4hr response SLA\n- Hardware Provisioning: Business Hours, IT Ops Team, 2 day SLA\n- Access Management: 24/7, Security Team, 1 hour SLA'
        };
      case 'team-settings':
        return {
          title: 'Team Settings Guidance',
          points: [
            'Configure workload balancing (round-robin, skill-based, balanced)',
            'Set up agent availability calendars',
            'Define skill matrix for skill-based routing',
            'Document team member roles and specializations'
          ],
          placeholderDetails: 'Example:\n- Round-robin for Tier 1 (all team members equal)\n- Skill-based for specialized requests (networking → John, databases → Sarah)\n- Agent availability: Track PTO, shifts, on-call rotations'
        };
      case 'field-configuration':
        return {
          title: 'Field Configuration Guidance',
          points: [
            'Configure conditional field logic (show/hide based on other fields)',
            'Set up field dependencies and cascading selects',
            'Define field validation rules',
            'Document which fields are required vs optional'
          ],
          placeholderDetails: 'Example:\n- If Request Type = Hardware, show "Device Type" field\n- If Device Type = Laptop, show "Laptop Specifications" fields\n- Cost field required if >$500\n- Manager approval field required if Cost >$1000'
        };
      default:
        return {
          title: 'Configuration Guidance',
          points: ['Document your configuration decisions', 'Note any dependencies or prerequisites', 'Track implementation status'],
          placeholderDetails: 'Add your configuration details here...'
        };
    }
  };

  const guidance = getConfigurationGuidance();

  const handleReExport = async (changeOrderId: string) => {
    // To re-export, we need the draft data. Assuming it's in the artifact.
    // If we don't have the draft data, we might need to fetch it or trigger generation via backend if supported.
    // Based on ChangeOrderBuilder, generation requires the draft object.
    // If the backend stores the generated PDF, maybe we can just download it?
    // The prompt says: "Re-export MUST pass the existing changeOrderId so ref/date remain stable."
    // This implies triggering the generation process again.
    
    // Let's assume we can trigger a re-download if we have the data. 
    // If not, we might assume the artifact contains the `draft` that was used.
    
    const co = editedDetails.artifacts?.changeOrders?.find((c: any) => c.changeOrderId === changeOrderId);
    if (!co) {
        toast.error("Change order not found");
        return;
    }
    
    // We need to instantiate the builder or call the API directly.
    // Calling API directly mirrors ChangeOrderBuilder.handleDownload
    const toastId = toast.loading("Re-exporting change order...");
    
    try {
        const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${item.id}/change-orders`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                draft: co.draft, // Assuming the artifact stores the draft
                userId: currentUser.id,
                userName: currentUser.name,
                changeOrderId: changeOrderId
            })
        });

        if (!response.ok) {
            throw new Error('Failed to re-export change order');
        }

        const data = await response.json();
        
        // Helper to download base64 (copied from ChangeOrderBuilder)
        const downloadBase64 = (base64: string, filename: string, type: string) => {
            const byteCharacters = atob(base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type });
            
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        };

        // Trigger downloads
        downloadBase64(data.pdfBase64, `${data.ref}.pdf`, 'application/pdf');
        downloadBase64(data.docxBase64, `${data.ref}.docx`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
        
        toast.success("Change Order re-exported", { id: toastId });
    } catch (e) {
        console.error(e);
        toast.error("Failed to re-export change order", { id: toastId });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                {getStatusIcon(editedDetails.status)}
                {item.name}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {item.description}
              </DialogDescription>
            </div>
            {getStatusBadge(editedDetails.status)}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Configuration Guidance */}
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              <p className="mb-2">{guidance.title}</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {guidance.points.map((point, idx) => (
                  <li key={idx}>{point}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>

          {/* Status Selection */}
          <div>
            <Label htmlFor="status">Configuration Status</Label>
            <Select
              value={editedDetails.status}
              onValueChange={(value: any) => setEditedDetails({ ...editedDetails, status: value })}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="not-started">Not Started</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="ready-for-implementation">Ready for Implementation</SelectItem>
                <SelectItem value="in-review">In Review</SelectItem>
                {isConsultant && <SelectItem value="done">Done</SelectItem>}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-1">
              {editedDetails.status === 'done' && editedDetails.implementedAt && (
                <>Completed on {new Date(editedDetails.implementedAt).toLocaleDateString()} by {editedDetails.implementedBy?.userName || editedDetails.implementedBy?.userEmail}</>
              )}
              {editedDetails.status === 'in-review' && 'This item is being reviewed before final implementation'}
              {editedDetails.status === 'ready-for-implementation' && 'This item is fully planned and ready for a consultant to implement in Jira'}
              {editedDetails.status === 'in-progress' && 'Work in progress - gathering requirements and planning configuration'}
              {editedDetails.status === 'not-started' && 'No configuration work has started yet'}
            </p>
          </div>

          {/* Vendor Selection */}
          <div>
            <Label htmlFor="vendor">Target Platform (Optional)</Label>
            <Select
              value={editedDetails.vendor || ''}
              onValueChange={(value: any) => setEditedDetails({ ...editedDetails, vendor: value || undefined })}
            >
              <SelectTrigger id="vendor">
                <SelectValue placeholder="Select platform..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Generic/Platform Agnostic</SelectItem>
                <SelectItem value="jira">Jira / JSM (Jira Service Management)</SelectItem>
                <SelectItem value="servicenow">ServiceNow</SelectItem>
                <SelectItem value="zendesk">Zendesk</SelectItem>
                <SelectItem value="ivanti-neurons">Ivanti Neurons</SelectItem>
                <SelectItem value="bmc-helix">BMC Helix</SelectItem>
                <SelectItem value="freshservice">Freshservice</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-1">
              {editedDetails.vendor ? `Access ${
                editedDetails.vendor === 'jira' ? 'Jira/JSM' :
                editedDetails.vendor === 'servicenow' ? 'ServiceNow' :
                editedDetails.vendor === 'zendesk' ? 'Zendesk' :
                editedDetails.vendor === 'ivanti-neurons' ? 'Ivanti Neurons' :
                editedDetails.vendor === 'bmc-helix' ? 'BMC Helix' :
                editedDetails.vendor === 'freshservice' ? 'Freshservice' :
                'Custom'
              }-specific fields and AI-powered screenshot analysis` : 'Select a platform to access vendor-specific configuration fields'}
            </p>
          </div>

          {/* Configuration Details */}
          <div>
            <Label htmlFor="config-details">Configuration Details</Label>
            <Textarea
              id="config-details"
              value={editedDetails.configurationDetails || ''}
              onChange={(e) => setEditedDetails({ ...editedDetails, configurationDetails: e.target.value })}
              placeholder={guidance.placeholderDetails}
              rows={8}
              className="font-mono text-sm"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Document specific configuration decisions, values, and requirements
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes & Comments</Label>
            <Textarea
              id="notes"
              value={editedDetails.notes || ''}
              onChange={(e) => setEditedDetails({ ...editedDetails, notes: e.target.value })}
              placeholder="Add any additional notes, questions, or context..."
              rows={3}
            />
          </div>

          {/* Metadata */}
          {item.coreFeature && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This is a <strong>core JSM feature</strong> and should be configured in Phase {item.defaultPhase}.
              </AlertDescription>
            </Alert>
          )}

          {item.requiresAssets && (
            <Alert className="border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                This feature requires <strong>Jira Service Management Assets</strong> (additional license).
              </AlertDescription>
            </Alert>
          )}

          {/* Vendor Specific Form */}
          {editedDetails.vendor && (
            <Tabs defaultValue="general">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="vendor">Vendor Specific</TabsTrigger>
                <TabsTrigger value="discovery">
                  <Settings className="h-4 w-4 mr-2" />
                  Discovery
                </TabsTrigger>
                <TabsTrigger value="messages">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Messages
                </TabsTrigger>
                <TabsTrigger value="files">
                  <Upload className="h-4 w-4 mr-2" />
                  Files
                </TabsTrigger>
                {isConsultant && (
                    <TabsTrigger value="scope-review">
                        <History className="h-4 w-4 mr-2" />
                        Scope Review
                    </TabsTrigger>
                )}
              </TabsList>
              <TabsContent value="general">
                {/* General Configuration Details */}
                <div>
                  <Label htmlFor="config-details">Configuration Details</Label>
                  <Textarea
                    id="config-details"
                    value={editedDetails.configurationDetails || ''}
                    onChange={(e) => setEditedDetails({ ...editedDetails, configurationDetails: e.target.value })}
                    placeholder={guidance.placeholderDetails}
                    rows={8}
                    className="font-mono text-sm"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Document specific configuration decisions, values, and requirements
                  </p>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Notes & Comments</Label>
                  <Textarea
                    id="notes"
                    value={editedDetails.notes || ''}
                    onChange={(e) => setEditedDetails({ ...editedDetails, notes: e.target.value })}
                    placeholder="Add any additional notes, questions, or context..."
                    rows={3}
                  />
                </div>
              </TabsContent>
              <TabsContent value="vendor">
                <VendorSpecificForm
                  vendor={editedDetails.vendor}
                  category={item.category}
                  values={editedDetails.vendorSpecificData || {}}
                  onChange={(data) => setEditedDetails({ ...editedDetails, vendorSpecificData: data })}
                  accessToken={accessToken}
                />
              </TabsContent>
              <TabsContent value="discovery">
                <div className="space-y-6">
                  {/* Authority Mapping Summary (if available) */}
                  {editedDetails.artifacts?.authorityMapping && (
                    <div className="border-b pb-6 mb-6">
                      <h3 className="font-semibold mb-4 text-lg">Authority Mapping Summary</h3>
                      <AuthorityMappingSummary artifact={editedDetails.artifacts.authorityMapping} />
                    </div>
                  )}

                  <QuestionManager
                    configItemId={item.id}
                    companyId={companyId}
                    category={item.category}
                    accessToken={accessToken}
                    mode="config-item"
                  />
                  
                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-4">Client Answers</h3>
                    <DiscoveryQuestionsPanel
                      configItemId={item.id}
                      configItemName={item.name}
                      companyId={companyId}
                      sowId={''} 
                      accessToken={accessToken}
                      isConsultant={isConsultant}
                      currentUser={currentUser}
                      configItemCategory={item.category}
                    />
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="messages">
                <CIMessaging
                  companyId={companyId}
                  configItemId={item.id}
                  configItemName={item.name}
                  currentUser={currentUser}
                  accessToken={accessToken}
                />
              </TabsContent>
              <TabsContent value="files">
                <div className="space-y-4">
                  <AttachmentUpload
                    companyId={companyId}
                    configItemId={item.id}
                    accessToken={accessToken}
                    analyzeWithAI={true}
                    onUploadComplete={(attachment) => {
                      console.log('Attachment uploaded:', attachment);
                      // Show visualization if AI analysis is available
                      if (attachment.aiAnalysis) {
                        // Attachment data will be shown in the visualization below
                      }
                    }}
                  />
                  <CIAttachmentList
                    companyId={companyId}
                    configItemId={item.id}
                    accessToken={accessToken}
                  />
                </div>
              </TabsContent>
              <TabsContent value="scope-review">
                {reopeningCO ? (
                    <ChangeOrderBuilder
                        selectedDeltas={[]} 
                        companyId={companyId}
                        configItemId={item.id}
                        userId={currentUser.id}
                        userName={currentUser.name}
                        onBack={() => setReopeningCO(null)}
                        initialDraft={reopeningCO.draft}
                        existingChangeOrderId={reopeningCO.id}
                    />
                ) : (
                    <div className="space-y-8">
                        {/* Scope Review Section */}
                        <div className="space-y-4">
                            <div className="flex justify-between items-start">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <History className="h-5 w-5" />
                                    Scope Review
                                </h3>
                                
                                {/* Artifacts Status Diagnostics Panel */}
                                <div className="bg-slate-50 border rounded-md p-3 text-xs w-64">
                                    <h4 className="font-semibold mb-2 flex items-center gap-1">
                                        <Database className="h-3 w-3" /> Artifacts Status
                                    </h4>
                                    <div className="space-y-1">
                                        <div className="flex justify-between">
                                            <span>SOW Baseline:</span>
                                            {/* Note: We don't have direct access to SOW baseline here without checking SOW artifact. 
                                                Assuming artifact presence check is needed. 
                                                For now we check if Authority Mapping exists as proxy for "ready for baseline" or similar?
                                                The prompt asks: "Baseline present? (sow.artifacts.sowScopeBaseline yes/no)".
                                                We don't have 'sow' prop here. 
                                                We have 'editedDetails.artifacts'.
                                                We can check 'editedDetails.artifacts.authorityMapping'.
                                            */}
                                            <span className={editedDetails.artifacts?.['sowScopeBaseline'] ? "text-green-600 font-bold" : "text-gray-400"}>
                                                {editedDetails.artifacts?.['sowScopeBaseline'] ? "YES" : "NO"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Auth Mapping:</span>
                                            <span className={editedDetails.artifacts?.authorityMapping ? "text-green-600 font-bold" : "text-gray-400"}>
                                                {editedDetails.artifacts?.authorityMapping ? "YES" : "NO"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Scope Deltas:</span>
                                            <span className={editedDetails.artifacts?.scopeDelta ? "text-green-600 font-bold" : "text-gray-400"}>
                                                {editedDetails.artifacts?.scopeDelta ? "YES" : "NO"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Decisions:</span>
                                            <span className={editedDetails.artifacts?.scopeDeltaDecisions ? "text-green-600 font-bold" : "text-gray-400"}>
                                                {editedDetails.artifacts?.scopeDeltaDecisions ? "YES" : "NO"}
                                            </span>
                                        </div>
                                        <div className="flex justify-between border-t pt-1 mt-1">
                                            <span>Change Orders:</span>
                                            <span className="font-bold">
                                                {editedDetails.artifacts?.changeOrders?.length || 0}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <ScopeDeltaReview
                                companyId={companyId}
                                configItemId={item.id}
                                deltas={editedDetails.artifacts?.scopeDelta || []}
                                existingDecisions={editedDetails.artifacts?.scopeDeltaDecisions || {}}
                                userId={currentUser.id}
                                userName={currentUser.name}
                            />
                        </div>

                        {/* Existing Change Orders List */}
                        {editedDetails.artifacts?.changeOrders && editedDetails.artifacts.changeOrders.length > 0 && (
                            <div className="space-y-4 pt-4 border-t">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Existing Change Orders
                                </h3>
                                <div className="grid gap-3">
                                    {editedDetails.artifacts.changeOrders.map((co: any) => (
                                        <div key={co.changeOrderId} className="flex items-center justify-between p-4 border rounded-lg bg-slate-50">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold">{co.ref || 'CO-DRAFT'}</span>
                                                    <Badge variant="outline">{new Date(co.createdAt || Date.now()).toLocaleDateString()}</Badge>
                                                    <Badge className={co.status === 'approved' || co.status === 'applied' ? 'bg-green-500' : 'bg-blue-500'}>
                                                        {co.status || 'draft'}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {co.draft?.summary?.substring(0, 100)}...
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={() => setReopeningCO({id: co.changeOrderId, draft: co.draft})}>
                                                    Edit / Re-open
                                                </Button>
                                                <Button variant="outline" size="sm" onClick={() => handleReExport(co.changeOrderId)}>
                                                    <RefreshCw className="h-4 w-4 mr-2" />
                                                    Re-export
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {isConsultant && (editedDetails.status === 'ready-for-implementation' || editedDetails.status === 'in-review') && (
              <Button variant="secondary" onClick={handleMarkImplemented}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Done
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving || !hasChanges}>
              {saving ? 'Saving...' : 'Save Customization'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}